import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type AccentName,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, TIERS, ROUTER, CACHE, CODE, GUARD, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const burn = interpolate(frame, [40, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="EAKC · cost-aware retrieval" color={COLORS.amber} />
          <Big>
            RAG is{" "}
            <span style={{ color: COLORS.amber, opacity: 0.4 + burn * 0.6 }}>
              burning money
            </span>
            <br />
            <span style={{ fontSize: 52, color: COLORS.dim }}>
              same answer billed twice ·{" "}
              <span style={{ color: COLORS.red }}>"what is X?" → frontier model</span>
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM · three failure modes */
export const Scene02Problem: React.FC = () => {
  const frame = useCurrentFrame();
  const a = interpolate(frame, [20, 45], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const b = interpolate(frame, [55, 80], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c = interpolate(frame, [90, 115], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="three silent leaks" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <div style={{ opacity: a }}>
            <Panel accent="amber" title="1 · over-fetch" width={400}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.6, color: COLORS.text }}>
                top-10 "to be safe" — 2-3 chunks answer, you pay for the other 7
              </div>
            </Panel>
          </div>
          <div style={{ opacity: b }}>
            <Panel accent="amber" title="2 · no cache" width={400}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.6, color: COLORS.text }}>
                same question, ten minutes apart — <span style={{ color: COLORS.red }}>billed in full, twice</span>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: c }}>
            <Panel accent="amber" title="3 · no routing" width={400}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.6, color: COLORS.text }}>
                one expensive model for every query, factoid or thesis
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 30 }}>
          no error · no alert · the bill just grows
        </div>
      </AbsoluteFill>
      <Caption lines={PROBLEM} accent="amber" />
    </AbsoluteFill>
  );
};

/* 03 — TIERS · task-purpose model tiers */
export const Scene03Tiers: React.FC = () => {
  const frame = useCurrentFrame();
  const rows: { tier: string; cls: string; color: AccentName }[] = [
    { tier: "Routing", cls: "Flash · Haiku · 4o-mini", color: "cyan" },
    { tier: "General", cls: "Flash (default)", color: "cyan" },
    { tier: "Reasoning", cls: "Sonnet · Gemini 2.5 Pro", color: "lime" },
    { tier: "Generation", cls: "Sonnet · 2.5 Pro · Opus", color: "lime" },
    { tier: "Explore", cls: "Flash (cheap is correct)", color: "cyan" },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.tiers.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="cheap by default · escalate when earned" color={COLORS.cyan} />
        <Panel accent="cyan" title="five task-purpose tiers · who reads this output?" width={860}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 25, lineHeight: 1.0 }}>
            {rows.map((r, i) => {
              const op = interpolate(frame, [12 + i * 12, 32 + i * 12], [0, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });
              return (
                <div
                  key={r.tier}
                  style={{
                    opacity: op,
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "13px 4px",
                    borderBottom: i < rows.length - 1 ? `1px solid ${COLORS.line}` : "none",
                  }}
                >
                  <span style={{ color: COLORS[r.color], fontWeight: 700 }}>{r.tier}</span>
                  <span style={{ color: COLORS.dim }}>{r.cls}</span>
                </div>
              );
            })}
          </div>
        </Panel>
        <div style={{ fontFamily: FONTS.display, fontSize: 30, color: COLORS.amber, marginTop: 28 }}>
          ~40× input-token spread, cheapest → priciest
        </div>
      </AbsoluteFill>
      <Caption lines={TIERS} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 04 — ROUTER · 7-strategy complexity router */
export const Scene04Router: React.FC = () => {
  const frame = useCurrentFrame();
  const a = interpolate(frame, [20, 50], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const b = interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.router.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="query-router · depth is routed, not constant" color={COLORS.green} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <div style={{ opacity: a }}>
            <Panel accent="cyan" title="simple" width={460}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 23, lineHeight: 1.85, color: COLORS.text }}>
                <div>1 pass · top-<b style={{ color: COLORS.cyan }}>5</b></div>
                <div>budget <b style={{ color: COLORS.cyan }}>4,000</b> · temp 0.3</div>
                <div style={{ color: COLORS.dim }}>reasoning OFF</div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: b }}>
            <Panel accent="lime" title="deep_research" width={460}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 23, lineHeight: 1.85, color: COLORS.text }}>
                <div>3 passes · top-<b style={{ color: COLORS.lime }}>20</b></div>
                <div>budget <b style={{ color: COLORS.lime }}>16,000</b> · 5 steps</div>
                <div style={{ color: COLORS.dim }}>reasoning ON</div>
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 24, color: COLORS.dim, marginTop: 30 }}>
          7 strategies · a hard query can upgrade its own tier
        </div>
      </AbsoluteFill>
      <Caption lines={ROUTER} accent="green" />
    </AbsoluteFill>
  );
};

/* 05 — CACHE · two-tier semantic cache */
const CACHE_PIPE: PipeNode[] = [
  { label: "query", value: "EMBED", sub: "1536-dim", accent: "cyan" },
  { label: "cosine ≥ 0.97", value: "HIT?", sub: "vs stored", accent: "violet" },
  { label: "Redis · fast", value: "~5 ms", sub: "Upstash", accent: "green" },
  { label: "answer", value: "$0 LLM", sub: "no call", accent: "lime" },
];
export const Scene05Cache: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.cache.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="semantic cache · fail-closed" color={COLORS.violet} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={CACHE_PIPE} startFrame={6} stepFrames={22} nodeWidth={250} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 40 }}>
        re-synced source → invalidate rows · a missing signature is a MISS, never a stale hit
      </div>
    </AbsoluteFill>
    <Caption lines={CACHE} accent="violet" />
  </AbsoluteFill>
);

/* 06 — CODE · the real constants */
const TS = `// src/lib/rag/semantic-cache.ts
const CACHE_TTL_MS        = 30 * 60 * 1000; // 30 min
const MAX_ENTRIES_PER_TENANT = 100;         // true LRU
const SIMILARITY_THRESHOLD   = 0.97;        // same query, reworded

// src/lib/rag/query-router.ts — one recipe per strategy
simple:        { passes: 1, top_k: 5,  budget: 4000,  reasoning: false }
deep_research: { passes: 3, top_k: 20, budget: 16000, reasoning: true  }

// src/lib/rag/embeddings.ts — cheaper AND better
EMBEDDING_MODEL = 'qwen/qwen3-embedding-8b'; // $0.01/M  (50% cheaper)
EMBEDDING_DIMENSIONS = 1536;                 // +8 MTEB, #1 on code`;
export const Scene06Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={TS}
        language="typescript"
        filename="semantic-cache.ts · query-router.ts · embeddings.ts"
        highlightLines={[4, 8, 9, 12, 13]}
        revealFrames={380}
        fontSize={23}
        width={1340}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 07 — GUARD · the ledger + the $55 incident */
export const Scene07Guard: React.FC = () => {
  const frame = useCurrentFrame();
  const g1 = interpolate(frame, [150, 185], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const g2 = interpolate(frame, [250, 285], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.guard.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="record what you spend · then stop the loop" color={COLORS.amber} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 26 }}>
          <MetricCounter value={55} startFrame={10} durationFrames={55} accent="red" prefix="$" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 54, color: COLORS.dim }}>
            billed ·{" "}
            <span style={{ color: COLORS.amber }}>$1.80</span> shown
          </span>
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 36 }}>
          <div style={{ opacity: g1 }}>
            <Callout label="the blind spot" accent="red" width={400}>
              cost recorded only on <b style={{ color: COLORS.text }}>success</b> — failed retry loops spent invisibly
            </Callout>
          </div>
          <div style={{ opacity: g2 }}>
            <Callout label="the fix · 3 guards" accent="green" width={400}>
              ledger every call · skip paused + cron-NULL · retry cap 3 · quota → <b style={{ color: COLORS.text }}>402</b>
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={GUARD} accent="amber" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          RAG gets you the right answers.
          <br />
          <span style={{ color: COLORS.cyan }}>This gets you the right bill.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 23, letterSpacing: "0.22em", color: COLORS.dim, marginTop: 42 }}>
          EAKC · cost-aware retrieval · no fabricated %
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
